import {
  adminCreateGarageInput,
  type GarageRole,
  garageNameProblem,
  normalizeGarageName,
} from "@bt/domain";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ExternalLinkIcon,
  PencilIcon,
  PlusIcon,
  TriangleAlertIcon,
  WarehouseIcon,
  XIcon,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/empty-state";
import { Field } from "@/components/app/field";
import { Page, PageHeader } from "@/components/app/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Members, RoleChoice } from "@/features/members/members";
import { toLanguage } from "@/i18n";
import { errorCode, errorMessage } from "@/lib/errors";
import { useFormat } from "@/lib/format";
import { garageUrl } from "@/lib/garage";
import { navigate, PageLink } from "@/lib/pages";
import { type AdminOutputs, useAdminTRPC } from "@/lib/trpc";
import { FormError } from "../account/sign-in";
import { GarageScope } from "./admin-app";

type AdminGarage = AdminOutputs["garages"]["list"][number];

function NoGarageAdmin() {
  const { t } = useTranslation();
  return (
    <Badge tone="missing">
      <TriangleAlertIcon />
      {t("admin.noGarageAdmin")}
    </Badge>
  );
}

function GarageFacts({ garage }: { garage: AdminGarage }) {
  const { t } = useTranslation();
  const format = useFormat();
  return (
    <p className="text-xs text-muted-foreground">
      {[
        t("admin.membersCount", { count: garage.members }),
        t("admin.garageAdminsCount", { count: garage.garageAdmins }),
        garage.pendingInvitations
          ? t("admin.invitedCount", { count: garage.pendingInvitations })
          : null,
        t("admin.createdOn", { date: format.date(garage.createdAt) }),
      ]
        .filter(Boolean)
        .join(" · ")}
    </p>
  );
}

/** Every garage, with "New garage" and its first users. */
export function GaragesPage() {
  const { t } = useTranslation();
  const trpc = useAdminTRPC();
  const { data: garages } = useQuery(trpc.garages.list.queryOptions());
  const [creating, setCreating] = useState(false);
  return (
    <Page>
      <PageHeader
        title={t("admin.garages")}
        subtitle={t("admin.garagesBody")}
        actions={
          <Button onClick={() => setCreating(true)}>
            <PlusIcon />
            {t("admin.newGarage")}
          </Button>
        }
      />
      {garages && !garages.length ? (
        <EmptyState icon={WarehouseIcon} title={t("admin.noGarages")} />
      ) : null}
      {garages?.length ? (
        <Card className="divide-y divide-border">
          {garages.map((garage) => (
            <PageLink
              key={garage.name}
              href={`/admin/garages/${garage.name}`}
              className="flex items-center gap-3 px-4 py-3 transition-colors outline-none hover:bg-muted/60 focus-visible:bg-muted"
            >
              <WarehouseIcon className="size-5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{garage.name}</p>
                <GarageFacts garage={garage} />
              </div>
              {garage.garageAdmins === 0 ? <NoGarageAdmin /> : null}
            </PageLink>
          ))}
        </Card>
      ) : null}
      <NewGarageDialog open={creating} onOpenChange={setCreating} />
    </Page>
  );
}

interface PersonDraft {
  email: string;
  firstName: string;
  lastName: string;
  role: GarageRole;
}

const emptyPerson = (role: GarageRole): PersonDraft => ({
  email: "",
  firstName: "",
  lastName: "",
  role,
});

function NewGarageDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent wide>
        <DialogHeader title={t("admin.newGarageTitle")} description={t("admin.newGarageBody")} />
        {open ? <NewGarageForm onDone={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function NewGarageForm({ onDone }: { onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const trpc = useAdminTRPC();
  const create = useMutation({ ...trpc.garages.create.mutationOptions(), meta: { silent: true } });
  const [name, setName] = useState("");
  const [people, setPeople] = useState<PersonDraft[]>([emptyPerson("garage_admin")]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const slug = normalizeGarageName(name);
  const problem = name ? garageNameProblem(slug) : null;
  const nameProblem = problem
    ? problem.kind === "reserved"
      ? t("garages.reserved", { word: problem.word })
      : t(`garages.${problem.kind}`)
    : undefined;

  const setPerson = (index: number, change: Partial<PersonDraft>) =>
    setPeople((list) => list.map((p, i) => (i === index ? { ...p, ...change } : p)));

  return (
    <form
      className="contents"
      onSubmit={async (event) => {
        event.preventDefault();
        const parsed = adminCreateGarageInput.safeParse({
          name,
          users: people,
          language: toLanguage(i18n.language),
        });
        const found: Record<string, string> = {};
        if (!slug || problem) found.name = nameProblem ?? "validation.required";
        for (const issue of parsed.success ? [] : parsed.error.issues) {
          found[issue.path.join(".")] = issue.message;
        }
        setErrors(found);
        if (Object.keys(found).length || !parsed.success) return;
        setServerError(null);
        try {
          const created = await create.mutateAsync(parsed.data);
          if (created.unsent.length) {
            toast.warning(t("admin.unsent", { emails: created.unsent.join(", ") }));
          } else toast.success(t("admin.garageCreated"));
          onDone();
          navigate(`/admin/garages/${created.name}`);
        } catch (error) {
          setServerError(
            errorCode(error) === "GARAGE_NAME_TAKEN" ? t("garages.taken") : errorMessage(error, t),
          );
        }
      }}
    >
      <DialogBody className="flex flex-col gap-5">
        <Field
          label={t("common.name")}
          htmlFor="new-garage-name"
          error={errors.name}
          hint={slug ? `${window.location.host}${garageUrl(slug)}` : t("garages.nameHint")}
        >
          <Input
            id="new-garage-name"
            autoFocus
            autoComplete="off"
            maxLength={200}
            value={name}
            aria-invalid={errors.name ? true : undefined}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <fieldset className="flex flex-col gap-3">
          <legend className="mb-2 text-sm font-medium">{t("admin.firstUsers")}</legend>
          {people.map((person, index) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: rows have no id until they are sent
              key={index}
              className="flex flex-col gap-3 rounded-lg border border-border p-3"
            >
              <div className="flex items-start gap-2">
                <Field
                  label={t("account.email")}
                  htmlFor={`person-${index}-email`}
                  error={errors[`users.${index}.email`]}
                  className="flex-1"
                >
                  <Input
                    id={`person-${index}-email`}
                    inputMode="email"
                    autoComplete="off"
                    value={person.email}
                    aria-invalid={errors[`users.${index}.email`] ? true : undefined}
                    onChange={(e) => setPerson(index, { email: e.target.value })}
                  />
                </Field>
                {people.length > 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="mt-6"
                    aria-label={t("admin.removePerson")}
                    onClick={() => setPeople((list) => list.filter((_, i) => i !== index))}
                  >
                    <XIcon />
                  </Button>
                ) : null}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label={t("account.firstName")}
                  htmlFor={`person-${index}-first`}
                  error={errors[`users.${index}.firstName`]}
                >
                  <Input
                    id={`person-${index}-first`}
                    autoComplete="off"
                    value={person.firstName}
                    onChange={(e) => setPerson(index, { firstName: e.target.value })}
                  />
                </Field>
                <Field
                  label={t("account.lastName")}
                  htmlFor={`person-${index}-last`}
                  error={errors[`users.${index}.lastName`]}
                >
                  <Input
                    id={`person-${index}-last`}
                    autoComplete="off"
                    value={person.lastName}
                    onChange={(e) => setPerson(index, { lastName: e.target.value })}
                  />
                </Field>
              </div>
              <RoleChoice
                value={person.role}
                onChange={(role) => setPerson(index, { role })}
                label={t("members.role")}
              />
            </div>
          ))}
          {errors.users ? (
            <p className="text-xs text-destructive" role="alert">
              {t(errors.users as "validation.required")}
            </p>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => setPeople((list) => [...list, emptyPerson("garage_user")])}
          >
            <PlusIcon />
            {t("admin.addPerson")}
          </Button>
        </fieldset>
        <FormError message={serverError} />
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onDone}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={create.isPending}>
          {t("admin.createAndInvite")}
        </Button>
      </DialogFooter>
    </form>
  );
}

/** One garage: its facts, renaming, and its members and invitations. */
export function GaragePage({ name }: { name: string }) {
  const { t } = useTranslation();
  const trpc = useAdminTRPC();
  const { data: garage, error } = useQuery({
    ...trpc.garages.get.queryOptions({ name }),
    retry: false,
  });
  const [renaming, setRenaming] = useState(false);
  if (error) {
    return (
      <Page>
        <EmptyState icon={WarehouseIcon} title={errorMessage(error, t)} className="mt-10" />
      </Page>
    );
  }
  if (!garage) return null;
  return (
    <Page>
      <PageLink
        href="/admin"
        className="mb-3 inline-block text-[13px] text-muted-foreground hover:text-foreground"
      >
        ← {t("admin.garages")}
      </PageLink>
      <PageHeader
        title={garage.name}
        subtitle={
          <div className="flex flex-col gap-1.5">
            <GarageFacts garage={garage} />
            {garage.garageAdmins === 0 ? (
              <div>
                <NoGarageAdmin />
              </div>
            ) : null}
          </div>
        }
        actions={
          <>
            <Button variant="outline" onClick={() => setRenaming(true)}>
              <PencilIcon />
              {t("admin.renameGarage")}
            </Button>
            <Button asChild>
              <a href={garageUrl(garage.name)}>
                <ExternalLinkIcon />
                {t("admin.openGarage")}
              </a>
            </Button>
          </>
        }
      />
      <h2 className="mb-1 text-lg font-semibold tracking-tight">{t("members.title")}</h2>
      <p className="mb-3 text-sm text-muted-foreground">{t("members.body")}</p>
      <GarageScope key={garage.name} name={garage.name}>
        <Members garageName={garage.name} />
      </GarageScope>
      <RenameDialog garage={garage.name} open={renaming} onOpenChange={setRenaming} />
    </Page>
  );
}

function RenameDialog({
  garage,
  open,
  onOpenChange,
}: {
  garage: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const trpc = useAdminTRPC();
  const rename = useMutation({ ...trpc.garages.rename.mutationOptions(), meta: { silent: true } });
  const [text, setText] = useState(garage);
  const [error, setError] = useState<string | null>(null);
  const slug = normalizeGarageName(text);
  const problem = garageNameProblem(slug);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader title={t("garages.renameTitle")} />
        <form
          className="contents"
          onSubmit={async (event) => {
            event.preventDefault();
            if (problem) {
              setError(
                problem.kind === "reserved"
                  ? t("garages.reserved", { word: problem.word })
                  : t(`garages.${problem.kind}`),
              );
              return;
            }
            try {
              const renamed = await rename.mutateAsync({ garage, name: text });
              onOpenChange(false);
              navigate(`/admin/garages/${renamed.name}`, { replace: true });
            } catch (e) {
              setError(
                errorCode(e) === "GARAGE_NAME_TAKEN" ? t("garages.taken") : errorMessage(e, t),
              );
            }
          }}
        >
          <DialogBody className="flex flex-col gap-4">
            <Field
              label={t("common.name")}
              htmlFor="admin-rename"
              hint={`${window.location.host}${garageUrl(slug || "…")}`}
            >
              <Input
                id="admin-rename"
                autoFocus
                autoComplete="off"
                maxLength={200}
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  setError(null);
                }}
              />
            </Field>
            <p className="flex items-start gap-2 rounded-lg border border-partial/40 bg-partial-bg px-3 py-2 text-sm text-partial-fg">
              <TriangleAlertIcon className="mt-0.5 size-4 shrink-0" />
              {t("garages.renameWarning")}
            </p>
            <FormError message={error} />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={rename.isPending}>
              {t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
